import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES, ECO_LOJA_LOGO, priceLabel, whatsappEcoLojaLink } from "../lib/constants";
import { ArrowUpRight } from "lucide-react";

export default function EcoLoja() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("todos");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (active === "todos" ? products : products.filter((p) => p.category === active)),
    [products, active]
  );

  return (
    <div data-testid="eco-loja-page">
      {/* Hero */}
      <section className="bg-cream-200 py-20">
        <div className="container-page flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="flex items-center gap-4 mb-5">
              <img src={ECO_LOJA_LOGO} alt="Eco Loja Tapajós" className="h-20 w-auto" />
              <div>
                <div className="eyebrow">Eco Loja</div>
                <div className="font-display text-2xl text-terracotta-700">Tapajós</div>
              </div>
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-forest-900 leading-[1.02] max-w-2xl">
              A floresta, feita <span className="italic text-copaiba-700">à mão</span>, entregue até você.
            </h1>
          </div>
          <p className="max-w-md text-forest-900/80 leading-relaxed">
            A Eco Loja reúne o melhor da bioeconomia da Flona do Tapajós: <strong>óleos vegetais amazônicos</strong>
            (copaíba, andiroba e outros), <strong>mel de abelhas da floresta</strong>, <strong>artesanato</strong> em
            sementes e fibras nativas e os <strong>móveis da Movelaria Anambé</strong>. Os valores variam conforme
            volume, peça ou encomenda — fale conosco pelo WhatsApp para consultar <strong>disponibilidade e preço</strong>.
            <br />
            <a
              href={whatsappEcoLojaLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="ecoloja-whatsapp-header"
              className="mt-3 inline-flex items-center gap-2 text-forest-700 hover:text-copaiba-700 font-medium"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.67 4.07 1.83 5.68L2 22l4.55-1.94c1.55.85 3.32 1.34 5.49 1.34 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.79 14.03c-.24.68-1.42 1.31-1.96 1.35-.5.05-1.14.06-1.83-.12-.42-.11-.96-.29-1.66-.6-2.94-1.27-4.86-4.24-5.01-4.43-.14-.19-1.19-1.58-1.19-3.03 0-1.44.76-2.15 1.03-2.44.27-.29.59-.36.79-.36h.56c.18 0 .43-.07.66.5.24.6.83 2.05.9 2.2.07.14.12.32.02.51-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.14.14-.29.29-.13.57.17.29.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.36 1.46.29.14.46.12.63-.07.17-.19.72-.84.91-1.13.19-.29.38-.24.65-.14.27.1 1.71.8 2 .95.29.14.48.22.55.34.07.12.07.69-.17 1.37Z"/></svg>
              WhatsApp Eco Loja / Movelaria: (93) 99117‑7997
            </a>
          </p>
        </div>

        <div className="container-page mt-10 flex flex-wrap gap-2 scroll-x overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              data-testid={`filter-${c.key}`}
              className={`whitespace-nowrap px-5 py-2.5 text-sm border transition-all duration-300 ${
                active === c.key
                  ? "bg-forest-700 text-cream-100 border-forest-700"
                  : "bg-white text-forest-700 border-cream-400 hover:bg-cream-300"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="py-20">
        <div className="container-page">
          {loading ? (
            <p className="text-forest-900/60">Carregando produtos...</p>
          ) : filtered.length === 0 ? (
            <p className="text-forest-900/60">Nenhum produto nesta categoria ainda.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <div key={p.id} className="card-earthy flex flex-col" data-testid={`product-card-${p.slug}`}>
                  <Link to={`/eco-loja/${p.slug}`} className="block group">
                    <div className="aspect-[4/5] overflow-hidden">
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="eyebrow mb-1">{p.category}</div>
                    <Link to={`/eco-loja/${p.slug}`}>
                      <h3 className="font-display text-2xl text-forest-900 leading-tight hover:text-copaiba-700 transition-colors">
                        {p.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-forest-900/70 mt-2 line-clamp-3 flex-1">{p.description}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <div className="font-display text-2xl text-copaiba-700">{priceLabel(p.price)}</div>
                      <Link
                        to={`/eco-loja/${p.slug}`}
                        data-testid={`product-detail-link-${p.slug}`}
                        className="text-sm inline-flex items-center gap-1 text-forest-700 hover:text-copaiba-700"
                      >
                        ver detalhes <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                    <a
                      href={whatsappEcoLojaLink(`Olá! Tenho interesse no produto: ${p.name}. Poderia me informar disponibilidade e valor?`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`whatsapp-buy-${p.slug}`}
                      className="btn-whatsapp mt-4"
                    >
                      Consultar pelo WhatsApp
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
